import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CloudOff, RefreshCw, X, AlertTriangle } from "lucide-react";
import { useProgress } from "../context/ProgressContext";
import { useCountdown, fmtCountdown } from "../lib/useCountdown";

const HOUR = 3600000, DAY = 86400000;
const KEY = "tycc_queue_banner_dismissed";

// One-line dashboard banner listing everything still waiting to send after a long offline stretch (> 1h).
const QueueBanner = () => {
  const { isCloud, syncStatus, pendingChanges, retrySync } = useProgress();
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(KEY) === "1");
  const { pending, online, saving, oldestPendingAt, retryAt, authExpired } = syncStatus;
  const left = useCountdown(retryAt);
  const age = oldestPendingAt ? Date.now() - oldestPendingAt : 0;
  if (!isCloud || dismissed || pending === 0 || (age < HOUR && !authExpired && left === null)) return null;

  const { children, cheers } = pendingChanges();
  const parts = children.map(({ child, changes }) => `${child.panda?.name} · ${changes.slice(0, 2).join(", ")}${changes.length > 2 ? ` +${changes.length - 2}` : ""}`);
  const nCheers = cheers.filter((q) => q.kind !== "reply").length;
  const nReplies = cheers.filter((q) => q.kind === "reply").length;
  if (nCheers) parts.push(`${nCheers} cheer${nCheers === 1 ? "" : "s"}`);
  if (nReplies) parts.push(`${nReplies} thank-you${nReplies === 1 ? "" : "s"}`);
  const stale = age > DAY;
  const days = Math.floor(age / DAY);

  return (
    <div role="status" className={`rounded-2xl border px-4 py-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm ${stale ? "bg-[#fde2e2] border-[#fbcaca] text-[#9b1c1c]" : "bg-[#fff3d6] border-[#ffe7b0] text-[#8a5a00]"}`} data-testid="queue-banner" data-stale={stale ? "true" : "false"}>
      {stale ? <AlertTriangle size={16} className="shrink-0" /> : <CloudOff size={16} className="shrink-0" />}
      <p className="flex-1 min-w-[200px]" data-testid="queue-banner-text">
        <span className="font-bold">{stale ? `Unsaved for ${days} day${days === 1 ? "" : "s"}: ` : "Waiting to send: "}</span>
        {parts.join(", ")}
        <span className="opacity-80" data-testid="queue-banner-status"> — {authExpired ? "please log in again to save." : online ? (left !== null ? `retrying in ${fmtCountdown(left)}…` : "the last save didn't go through.") : "will send when you're online."}</span>
      </p>
      {authExpired ? (
        <Link to="/login" className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold text-white bg-[#7d5fb2] hover:bg-[#6a4fa0]" data-testid="queue-banner-login">Log in again</Link>
      ) : online && (
        <button type="button" onClick={retrySync} disabled={saving} className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold text-white disabled:opacity-60 ${stale ? "bg-[#9b1c1c] hover:bg-[#7f1d1d]" : "bg-[#1b8bc9] hover:bg-[#166fa3]"}`} data-testid="queue-banner-retry">
          <RefreshCw size={12} className={saving ? "animate-spin" : ""} /> {saving ? "Sending..." : "Retry now"}
        </button>
      )}
      <button type="button" onClick={() => { sessionStorage.setItem(KEY, "1"); setDismissed(true); }} className="p-1 rounded-full hover:bg-black/5" aria-label="Dismiss" data-testid="queue-banner-dismiss"><X size={14} /></button>
    </div>
  );
};

export default QueueBanner;
